import 'isomorphic-fetch';
import { removeWhitespaceFromValues, objectToQueryString } from 'common/utils';
import { splitTreatmentIndex } from './SplitTreatmentIndex';
import { AgeVerificationUtility } from '../apps/age_verification/utilities';

const sanitizeAddOns = addOns => addOns.filter(addOn => addOn.quantity > 0);
export const offlineError =
  'Error: no response received from request. The browser may be offline';
const MEMBERSHIPS_API = '/api/v1/memberships';

export class ApiError extends Error {
  constructor(error, ...params) {
    const message = error.message || `Error: ${error}`;

    super(message, ...params);

    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApiError);
    }

    this.data = error;
  }
}

export class ApiClient {
  constructor() {
    this.setCsrfToken();
  }

  setCsrfToken() {
    this.csrfToken = this.csrfToken || this.getCsrfToken();
  }

  getCsrfToken() {
    return (global.document.querySelector('meta[name="csrf-token"]') || {})
      .content;
  }

  makeRequest(url, options) {
    return fetch(url, this.buildRequestOptions(options));
  }

  buildRequestOptions(options = {}) {
    return {
      ...options,
      credentials: 'same-origin',
      headers: {
        ...options.headers,
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': this.csrfToken,
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    };
  }

  handleResponse(request) {
    return request.then(
      response => {
        const contentType = response.headers.get('content-type');
        const isJson = contentType && contentType.includes('application/json');
        const contentPromise = isJson ? response.json() : response.text();

        if (response.status >= 400) {
          return contentPromise.then(content => {
            throw new ApiError(content.error || content);
          });
        }

        return contentPromise;
      },
      () => {
        throw new ApiError(offlineError);
      }
    );
  }

  // API calls.

  createAddress = address => {
    const body = removeWhitespaceFromValues(address);
    const request = this.makeRequest('/api/v1/user_addresses', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return this.handleResponse(request);
  };

  updateAddress = address => {
    const body = removeWhitespaceFromValues(address);
    const request = this.makeRequest(`/api/v1/user_addresses/${address.id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    return this.handleResponse(request);
  };

  deleteAddress = id => {
    const request = this.makeRequest(`/api/v1/user_addresses/${id}`, {
      method: 'DELETE',
    });
    return this.handleResponse(request);
  };

  fetchAddress = id => {
    const request = this.makeRequest(`/api/v1/user_addresses/${id}`);
    return this.handleResponse(request);
  };

  fetchAddresses = () => {
    const request = this.makeRequest('/api/v1/user_addresses');
    return this.handleResponse(request);
  };

  fetchPods = () => {
    const request = this.makeRequest('/api/v1/pods');
    return this.handleResponse(request);
  };

  fetchSubscription = () => {
    const request = this.makeRequest('/api/v1/subscription');
    return this.handleResponse(request);
  };

  fetchSplitTreatments = () => {
    const params = splitTreatmentIndex.map(f => `features[]=${f}`).join('&');
    const request = this.makeRequest(`/api/v1/split?${params}`);
    return this.handleResponse(request);
  };

  fetchComplianceRestrictedStates = () => {
    const request = this.makeRequest(
      '/api/v1/subscription/compliance_restricted_states'
    );
    return this.handleResponse(request);
  };

  updateSubscription = (subscription, changedFields) => {
    if (changedFields.add_ons) {
      changedFields.add_ons = sanitizeAddOns(changedFields.add_ons);
    }

    const request = this.makeRequest('/api/v1/subscription', {
      method: 'PATCH',
      body: JSON.stringify({
        uuid: subscription.uuid,
        ...changedFields,
      }),
    });
    return this.handleResponse(request);
  };

  updateSubscriptionPods = subscription => {
    const request = this.makeRequest('/api/v1/subscription_pod_swap', {
      method: 'PUT',
      body: JSON.stringify({ uuid: subscription.uuid }),
    });
    return this.handleResponse(request);
  };

  cancelSubscription = (subscription, cancelReasons, cancelDetails) => {
    const request = this.makeRequest('/api/v1/subscription/cancel', {
      body: JSON.stringify({
        cancel_details: cancelDetails,
        cancel_reasons: cancelReasons,
      }),
      method: 'POST',
    });
    return this.handleResponse(request);
  };

  createMembership = () => {
    const request = this.makeRequest(MEMBERSHIPS_API, {
      method: 'POST',
      body: null,
    });
    return this.handleResponse(request);
  };

  updateMembership = changedFields => {
    const request = this.makeRequest(MEMBERSHIPS_API, {
      method: 'PATCH',
      body: JSON.stringify({ ...changedFields }),
    });
    return this.handleResponse(request);
  };

  fetchMembership = () => {
    const request = this.makeRequest(MEMBERSHIPS_API);
    return this.handleResponse(request);
  };

  fetchRegisteredProducts = () => {
    const request = this.makeRequest('/api/v1/product_registrations');
    return this.handleResponse(request);
  };

  fetchRegisteredProductsWarranty = productType => {
    const request = this.makeRequest(
      productType
        ? `/api/v1/product_registrations/warranty_eligible_by_product/${productType}`
        : '/api/v1/product_registrations/warranty_eligible'
    );
    return this.handleResponse(request);
  };

  fetchWarrantySkus = productType => {
    const request = this.makeRequest(`/api/warranty/skus/${productType}`);
    return this.handleResponse(request);
  };

  fetchWarrantyIssues = claimWarrantySkuId => {
    const request = this.makeRequest(
      `/api/warranty/issues/${claimWarrantySkuId}`
    );
    return this.handleResponse(request);
  };

  createProductRegistration = product => {
    const request = this.makeRequest('/api/v1/product_registrations', {
      method: 'POST',
      body: JSON.stringify({
        product: removeWhitespaceFromValues(product),
      }),
    });
    return this.handleResponse(request);
  };

  createSmsSubscription = () => {
    const request = this.makeRequest('/api/v1/sms_subscription', {
      method: 'POST',
      body: null,
    });
    return this.handleResponse(request);
  };

  createSubscriptionQuote = addOns => {
    addOns = sanitizeAddOns(addOns);

    const request = this.makeRequest('/api/v1/subscription_quotes', {
      method: 'POST',
      body: JSON.stringify({ add_ons: addOns }),
    });
    return this.handleResponse(request);
  };

  reportDevice = params => {
    const request = this.makeRequest(`/api/traceability/report_device`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return this.handleResponse(request);
  };

  verifyRecaptchaToken = params => {
    const request = this.makeRequest(`/api/traceability/verify_recaptcha`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return this.handleResponse(request);
  };

  fetchProfile = () => {
    const request = this.makeRequest('/api/v1/profile');
    return this.handleResponse(request);
  };

  updateProfile = user => {
    const request = this.makeRequest('/api/v1/profile', {
      method: 'PUT',
      body: JSON.stringify({
        user: removeWhitespaceFromValues(user),
      }),
    });
    return this.handleResponse(request);
  };

  fetchOrder = orderNumber => {
    const request = this.makeRequest(`/api/v1/orders/${orderNumber}`);
    return this.handleResponse(request);
  };

  fetchCurrentOrder = () => {
    const request = this.makeRequest(`/api/v1/orders/current`);
    return this.handleResponse(request);
  };

  fetchOrders = (params = {}) => {
    let requestPath = '/api/v1/orders';
    const paramsAreEmpty = Object.keys(params).length === 0;

    if (!paramsAreEmpty) {
      const queryString = objectToQueryString(params);
      requestPath += `?${queryString}`;
    }

    const request = this.makeRequest(requestPath);
    return this.handleResponse(request);
  };

  fetchOrderSummary = () => {
    const request = this.makeRequest('/api/v1/orders/summary');
    return this.handleResponse(request);
  };

  fetchAvailablePaymentMethods = () => {
    const request = this.makeRequest('/api/v1/payment_methods');
    return this.handleResponse(request);
  };

  fetchVariants = () => {
    const request = this.makeRequest('/api/v1/variants');
    return this.handleResponse(request);
  };

  cancelOrder = orderNumber => {
    const request = this.makeRequest(`/api/v1/orders/${orderNumber}/cancel`, {
      method: 'POST',
    });
    return this.handleResponse(request);
  };

  changeOrderMembership = value => {
    const request = this.makeRequest('/api/v1/cart/membership', {
      method: 'PUT',
      body: JSON.stringify({ membership_order: value }),
    });
    return this.handleResponse(request);
  };

  updateCartLineItems = (lineItems, isCommitting) => {
    const url = `/api/v1/cart`;
    const data = { line_items: lineItems, committing: isCommitting };

    const request = this.makeRequest(url, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    return this.handleResponse(request);
  };

  fetchCartLineItemsCount = () => {
    const request = this.makeRequest('/api/v1/cart/items_count');

    return this.handleResponse(request);
  };

  fetchAgeVerification = () => {
    const request = this.makeRequest(`/api/v1/age_verification`);
    return this.handleResponse(request);
  };

  updateAgeVerification = data => {
    const coercedAVData = new AgeVerificationUtility(data)
      .createDateOfBirth()
      .coerceAddressState()
      .value();

    const request = this.makeRequest(`/api/v1/age_verification`, {
      method: 'PUT',
      body: JSON.stringify(coercedAVData),
    });
    return this.handleResponse(request);
  };

  ageVerificationReview = () => {
    const request = this.makeRequest(`/api/v1/age_verification/review`, {
      method: 'POST',
      body: '',
    });
    return this.handleResponse(request);
  };

  fetchClaimRequests = () => {
    const request = this.makeRequest('/api/v1/claim_requests');
    return this.handleResponse(request);
  };

  fetchCurrentClaimRequest = () => {
    const request = this.makeRequest('/api/v1/claim_requests/current');
    return this.handleResponse(request);
  };

  fetchClaimRequest = id => {
    const request = this.makeRequest(`/api/v1/claim_requests/${id}`);
    return this.handleResponse(request);
  };

  fetchProcessingClaimItems = () => {
    const request = this.makeRequest('/api/v1/claim_items/processing');
    return this.handleResponse(request);
  };

  createClaimRequest = createRequest => {
    const request = this.makeRequest('/api/v1/claim_requests', {
      method: 'POST',
      body: JSON.stringify({
        claim_request: {
          claim_item: createRequest,
        },
      }),
    });
    return this.handleResponse(request);
  };

  completeClaimRequest = (claimRequest, address) => {
    const request = this.makeRequest(
      `/api/v1/claim_requests/${claimRequest.id}/complete`,
      {
        method: 'POST',
        body: JSON.stringify({
          claim_request: {
            ship_address_id: address.id,
          },
        }),
      }
    );
    return this.handleResponse(request);
  };

  cancelClaimRequest = claimRequest => {
    const request = this.makeRequest(
      `/api/v1/claim_requests/${claimRequest.id}`,
      {
        method: 'DELETE',
      }
    );
    return this.handleResponse(request);
  };

  fetchBraintreeClientToken = () => {
    const request = this.makeRequest(
      '/solidus_paypal_braintree/client_token.json',
      {
        method: 'POST',
        body: '',
      }
    );

    return this.handleResponse(request);
  };

  fetchPaymentSources = () => {
    const request = this.makeRequest('/api/v1/payment_sources');
    return this.handleResponse(request);
  };

  addPaymentSource = params => {
    const request = this.makeRequest(`/api/v1/payment_sources.json`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return this.handleResponse(request);
  };

  startCheckout = location => {
    const request = this.makeRequest('/api/v1/checkout/start', {
      method: 'POST',
      body: JSON.stringify({
        location,
      }),
    });
    return this.handleResponse(request);
  };

  completeCheckout = params => {
    const request = this.makeRequest('/api/v1/checkout/complete', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return this.handleResponse(request);
  };

  offsiteCheckout = params => {
    const request = this.makeRequest('/api/v1/checkout/offsite', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return this.handleResponse(request);
  };

  recalculateOrder = params => {
    const request = this.makeRequest('/api/v1/checkout/recalculate_order', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return this.handleResponse(request);
  };

  fetchLegalPageContent = () => {
    const request = this.makeRequest('/api/v1/contentful_entries/legal');
    return this.handleResponse(request);
  };

  fetchBannerContent = () => {
    const request = this.makeRequest('/api/v1/contentful_entries/banner');
    return this.handleResponse(request);
  };

  phoneVerificationSendCode = (phone_number, country_iso_code, via) => {
    const request = this.makeRequest('/api/v1/phone_verification/send_code', {
      method: 'POST',
      body: JSON.stringify({
        phone_verification: {
          phone_number,
          country_iso_code,
          via,
        },
      }),
    });
    return this.handleResponse(request);
  };

  phoneVerificationVerifyCode = (phone_number, country_iso_code, code) => {
    const request = this.makeRequest('/api/v1/phone_verification/verify_code', {
      method: 'POST',
      body: JSON.stringify({
        phone_verification: {
          phone_number,
          country_iso_code,
          code,
        },
      }),
    });
    return this.handleResponse(request);
  };

  phoneVerificationCheckVerified = () => {
    const request = this.makeRequest(
      '/api/v1/phone_verification/check_verified'
    );
    return this.handleResponse(request);
  };

  sessionStateAbbr = value => {
    const request = this.makeRequest('/api/v1/session_state_abbr', {
      method: 'PUT',
      body: JSON.stringify({ state_abbr: value }),
    });
    return this.handleResponse(request);
  };

  fetchImpersonationStatus = () => {
    const request = this.makeRequest('/api/v1/impersonation');
    return this.handleResponse(request);
  };

  sendRetailersSupportRequest = params => {
    const request = this.makeRequest('/api/v1/retailers_support', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return this.handleResponse(request);
  };

  validateExchangedDevice = params => {
    const request = this.makeRequest(
      '/api/v1/warranty_exchanges/validate_exchanged_device',
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
    return this.handleResponse(request);
  };

  createDeviceWarrantyExchange = params => {
    const request = this.makeRequest(
      '/api/v1/warranty_exchanges/create_device_exchange',
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
    return this.handleResponse(request);
  };

  validateExchangedPod = params => {
    const request = this.makeRequest(
      '/api/v1/warranty_exchanges/validate_exchanged_pod',
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
    return this.handleResponse(request);
  };

  createPodWarrantyExchange = params => {
    const request = this.makeRequest(
      '/api/v1/warranty_exchanges/create_pod_exchange',
      {
        method: 'POST',
        body: JSON.stringify(params),
      }
    );
    return this.handleResponse(request);
  };

  fetchFDKPaymentOptions = () => {
    const request = this.makeRequest('/api/v1/fdk/payment_options');
    return this.handleResponse(request);
  };

  convertOrderToSubscription = params => {
    const request = this.makeRequest('/api/v1/one_time_order_conversion', {
      method: 'PUT',
      body: JSON.stringify(params),
    });
    return this.handleResponse(request);
  };

  fetchLossProtectionEligibleRegistrations = () => {
    const request = this.makeRequest(
      '/api/v1/product_registrations/loss_protection_eligible'
    );
    return this.handleResponse(request);
  };

  createLossProtectionClaim = params => {
    const request = this.makeRequest('/api/v1/loss_protection/complete', {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return this.handleResponse(request);
  };
}

const api = new ApiClient();

export default api;
